import React, { useState, useEffect, useCallback } from 'react';
import {
  LayoutDashboard, Shield, FileText, AlertCircle, CheckCircle2, XCircle, Clock, TrendingUp, TrendingDown, Users, Ticket, Calendar, Download, Filter, Search,
  RefreshCw, Eye, Settings, Plus, Edit, Trash2, Save, List, Bell, Mail, Phone, MessageSquare, Heart, Award, Target, Cpu, Database, Monitor, UserPlus, UserMinus, Send, Archive, Unarchive, History, Clock3, Star, Crown,
  Timer, Brain, Route, Lightning, HardDrive, MapPin, Globe, Key, Lock, Unlock, Smartphone, File, FileVideo, FileAudio, FileImage,
  FileSpreadsheet, FilePdf, FileZip, FileCheck, FileX, FilePlus, FileMinus, FileEdit, FileSearch, FileSlash, FileWarning,
  FileQuestion, FileHeart, FileClock, FileUp, FileDown, FileLeft, FileRight, FileBarChart, FilePieChart, FileLineChart,
  FileScissors, FileType, FileSymlink, FileStack, FileTree, FileBinary, FileCog, FileSettings, FileLock, FileUnlock,
  FileKey, FileSignature, FileVerified, FileBadge, FileTag, FileLabel, FileBookmark, FileStar, FileFlag,
  FileAlert, FileInfo, FileHelp, FileMessage, FileComment, FileChat, FileMail, FilePhone, Activity, BarChart3, PieChart, LineChart
} from 'lucide-react';
import { AdminService } from '../../services/AdminService';

const ComplianceReporting = () => {
  const [complianceData, setComplianceData] = useState(null);
  const [auditReports, setAuditReports] = useState([]);
  const [regulatoryReports, setRegulatoryReports] = useState([]);
  const [complianceAlerts, setComplianceAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState('30d'); // Default to 30 days
  const [selectedFramework, setSelectedFramework] = useState('all');
  const [showResolvedAlerts, setShowResolvedAlerts] = useState(false);

  const loadComplianceData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const complianceResponse = await new AdminService().getComplianceMetrics(timeRange, selectedFramework);
      const auditResponse = await new AdminService().getAuditReports();
      const regulatoryResponse = await new AdminService().getRegulatoryReports();
      const alertsResponse = await new AdminService().getComplianceAlerts();

      if (complianceResponse.success && auditResponse.success && regulatoryResponse.success && alertsResponse.success) {
        setComplianceData(complianceResponse.data);
        setAuditReports(auditResponse.data);
        setRegulatoryReports(regulatoryResponse.data);
        setComplianceAlerts(alertsResponse.data);
      } else {
        setError(complianceResponse.message || auditResponse.message || regulatoryResponse.message || alertsResponse.message);
      }
    } catch (err) {
      setError('Failed to fetch compliance data.');
      console.error('Error fetching compliance data:', err);
    } finally {
      setLoading(false);
    }
  }, [timeRange, selectedFramework]);

  useEffect(() => {
    loadComplianceData();
    const interval = setInterval(loadComplianceData, 60000); // Refresh every minute
    return () => clearInterval(interval);
  }, [loadComplianceData]);

  const handleGenerateReport = async (reportType, framework) => {
    if (window.confirm(`Are you sure you want to generate ${reportType} report for ${framework}?`)) {
      try {
        const response = await new AdminService().generateComplianceReport(reportType, framework);
        if (response.success) {
          alert(`${reportType} report generated successfully!`);
          loadComplianceData(); // Refresh data
        } else {
          alert(`Failed to generate report: ${response.message}`);
        }
      } catch (err) {
        alert('Error generating report.');
        console.error('Error generating report:', err);
      }
    }
  };

  const handleResolveAlert = async (alertId) => {
    if (window.confirm('Are you sure you want to resolve this compliance alert?')) {
      try {
        const response = await new AdminService().resolveComplianceAlert(alertId);
        if (response.success) {
          alert('Compliance alert resolved successfully!');
          setComplianceAlerts(prev => prev.filter(alert => alert.id !== alertId));
        } else {
          alert(`Failed to resolve alert: ${response.message}`);
        }
      } catch (err) {
        alert('Error resolving alert.');
        console.error('Error resolving alert:', err);
      }
    }
  };

  const getComplianceStatusColor = (status) => {
    switch (status) {
      case 'compliant': return 'text-green-600 bg-green-100';
      case 'warning': return 'text-yellow-600 bg-yellow-100';
      case 'non-compliant': return 'text-red-600 bg-red-100';
      case 'pending': return 'text-blue-600 bg-blue-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'low': return 'text-blue-600 bg-blue-100';
      case 'medium': return 'text-yellow-600 bg-yellow-100';
      case 'high': return 'text-orange-600 bg-orange-100';
      case 'critical': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  if (loading) {
    return <div className="p-6 text-center text-gray-500">Loading compliance data...</div>;
  }

  if (error) {
    return <div className="p-6 text-center text-red-500">Error: {error}</div>;
  }

  if (!complianceData) {
    return <div className="p-6 text-center text-gray-500">No compliance data available.</div>;
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-800 flex items-center">
          <Shield className="mr-3 text-blue-600" size={30} />
          Compliance Reporting Dashboard
        </h1>
        <div className="flex items-center space-x-4">
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="p-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
            <option value="90d">Last 90 Days</option>
            <option value="1y">Last Year</option>
          </select>
          <select
            value={selectedFramework}
            onChange={(e) => setSelectedFramework(e.target.value)}
            className="p-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Frameworks</option>
            <option value="gdpr">GDPR</option>
            <option value="sox">SOX</option>
            <option value="hipaa">HIPAA</option>
            <option value="iso27001">ISO 27001</option>
            <option value="pci-dss">PCI DSS</option>
          </select>
          <button
            onClick={loadComplianceData}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md shadow-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <RefreshCw className="mr-2" size={18} /> Refresh
          </button>
        </div>
      </div>

      <p className="text-gray-600 mb-8">
        Comprehensive compliance monitoring and reporting for regulatory requirements.
        <span className="ml-2 text-sm text-gray-500">Last Updated: {new Date(complianceData.lastUpdated).toLocaleString()}</span>
      </p>

      {/* Compliance Overview */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-700 mb-4 flex items-center">
          <BarChart3 className="mr-2 text-purple-600" size={24} /> Compliance Overview
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {complianceData.overviewMetrics.map((metric, index) => (
            <div key={index} className="bg-white p-6 rounded-lg shadow-md border border-gray-200 flex items-center justify-between">
              <div>
                <p className="text-lg font-medium text-gray-600">{metric.name}</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{metric.value}</p>
                <div className="flex items-center mt-2 text-sm">
                  <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getComplianceStatusColor(metric.status)}`}>
                    {metric.status}
                  </span>
                  <span className="ml-3 text-gray-500">{metric.description}</span>
                </div>
              </div>
              {metric.type === 'overall_score' && <Target size={40} className="text-blue-500 opacity-70" />}
              {metric.type === 'audit_findings' && <FileText size={40} className="text-red-500 opacity-70" />}
              {metric.type === 'policy_violations' && <AlertCircle size={40} className="text-yellow-500 opacity-70" />}
              {metric.type === 'certification_status' && <Award size={40} className="text-green-500 opacity-70" />}
            </div>
          ))}
        </div>
      </section>

      {/* Framework Compliance Status */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-700 mb-4 flex items-center">
          <Shield className="mr-2 text-green-600" size={24} /> Framework Compliance Status
        </h2>
        <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Framework
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Score
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Audit
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Next Due
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {complianceData.frameworkStatus.map((framework, index) => (
                <tr key={index}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{framework.name}</div>
                    <div className="text-sm text-gray-500">{framework.description}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getComplianceStatusColor(framework.status)}`}>
                      {framework.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div className="flex items-center">
                      <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                        <div 
                          className={`h-2 rounded-full ${framework.score >= 80 ? 'bg-green-500' : framework.score >= 60 ? 'bg-yellow-500' : 'bg-red-500'}`}
                          style={{ width: `${framework.score}%` }}
                        ></div>
                      </div>
                      <span className="text-sm font-medium">{framework.score}%</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(framework.lastAudit).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(framework.nextDue).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => handleGenerateReport('audit', framework.name)}
                      className="text-blue-600 hover:text-blue-900 mr-3"
                      title="Generate Audit Report"
                    >
                      <FileText size={18} />
                    </button>
                    <button
                      onClick={() => handleGenerateReport('regulatory', framework.name)}
                      className="text-green-600 hover:text-green-900"
                      title="Generate Regulatory Report"
                    >
                      <Award size={18} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Compliance Alerts */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-700 mb-4 flex items-center">
          <Bell className="mr-2 text-red-600" size={24} /> Compliance Alerts
        </h2>
        <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={showResolvedAlerts}
                    onChange={(e) => setShowResolvedAlerts(e.target.checked)}
                    className="mr-2"
                  />
                  Show Resolved Alerts
                </label>
              </div>
              <div className="text-sm text-gray-500">
                {complianceAlerts.filter(alert => showResolvedAlerts || alert.status !== 'resolved').length} active alerts
              </div>
            </div>
          </div>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Alert
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Framework
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Severity
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Detected
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {complianceAlerts.filter(alert => showResolvedAlerts || alert.status !== 'resolved').length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-4 text-center text-gray-500">No compliance alerts found.</td>
                </tr>
              ) : (
                complianceAlerts.filter(alert => showResolvedAlerts || alert.status !== 'resolved').map((alert) => (
                  <tr key={alert.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{alert.title}</div>
                      <div className="text-sm text-gray-500">{alert.description}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                        {alert.framework}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getSeverityColor(alert.severity)}`}>
                        {alert.severity}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(alert.detectedAt).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      {alert.status !== 'resolved' && (
                        <button
                          onClick={() => handleResolveAlert(alert.id)}
                          className="text-green-600 hover:text-green-900"
                          title="Resolve Alert"
                        >
                          <CheckCircle2 size={18} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Audit Reports */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-700 mb-4 flex items-center">
          <FileText className="mr-2 text-blue-600" size={24} /> Recent Audit Reports
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {auditReports.length === 0 ? (
            <div className="lg:col-span-3 p-6 text-center text-gray-500 bg-white rounded-lg shadow-md border border-gray-200">
              No audit reports available.
            </div>
          ) : (
            auditReports.map((report) => (
              <div key={report.id} className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
                <h3 className="text-xl font-medium text-gray-800 mb-2">{report.title}</h3>
                <p className="text-gray-600 text-sm mb-3">{report.description}</p>
                <div className="flex flex-wrap gap-2 mb-4">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${getComplianceStatusColor(report.status)}`}>
                    {report.status}
                  </span>
                  <span className="px-3 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
                    {report.framework}
                  </span>
                  <span className="px-3 py-1 bg-purple-100 text-purple-800 text-xs font-medium rounded-full">
                    {report.type}
                  </span>
                </div>
                <p className="text-sm text-gray-700 mb-2">Generated: <span className="font-semibold">{new Date(report.generatedAt).toLocaleDateString()}</span></p>
                <p className="text-sm text-gray-700 mb-4">Findings: <span className="font-semibold">{report.findingsCount}</span></p>
                <div className="flex space-x-2">
                  <button className="flex-1 flex items-center justify-center px-3 py-2 bg-blue-600 text-white rounded-md shadow-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <Eye className="mr-2" size={16} /> View
                  </button>
                  <button className="flex-1 flex items-center justify-center px-3 py-2 bg-green-600 text-white rounded-md shadow-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500">
                    <Download className="mr-2" size={16} /> Download
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </section>

      {/* Regulatory Reports */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-700 mb-4 flex items-center">
          <Award className="mr-2 text-green-600" size={24} /> Regulatory Reports
        </h2>
        <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Report Name
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Framework
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Due Date
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {regulatoryReports.map((report, index) => (
                <tr key={index}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{report.name}</div>
                    <div className="text-sm text-gray-500">{report.description}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                      {report.framework}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getComplianceStatusColor(report.status)}`}>
                      {report.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(report.dueDate).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => handleGenerateReport('regulatory', report.framework)}
                      className="text-blue-600 hover:text-blue-900 mr-3"
                      title="Generate Report"
                    >
                      <Award size={18} />
                    </button>
                    <button className="text-green-600 hover:text-green-900" title="Download Report">
                      <Download size={18} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Compliance Trends */}
      <section>
        <h2 className="text-2xl font-semibold text-gray-700 mb-4 flex items-center">
          <TrendingUp className="mr-2 text-purple-600" size={24} /> Compliance Trends
        </h2>
        <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {complianceData.trendMetrics.map((trend, index) => (
              <div key={index} className="text-center">
                <div className="text-2xl font-bold text-gray-900 mb-1">{trend.value}</div>
                <div className="text-sm text-gray-600 mb-2">{trend.name}</div>
                <div className="flex items-center justify-center text-sm">
                  {trend.change > 0 ? (
                    <TrendingUp className="text-green-500 mr-1" size={16} />
                  ) : (
                    <TrendingDown className="text-red-500 mr-1" size={16} />
                  )}
                  <span className={trend.change > 0 ? 'text-green-600' : 'text-red-600'}>
                    {Math.abs(trend.change)}%
                  </span>
                  <span className="ml-1 text-gray-500">vs last period</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ComplianceReporting;
